import React, { createContext, useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  applyAuthTokensFromResponse,
  clearAuthTokens,
  getAccessToken,
  getRefreshToken,
} from '../../utils/authTokenStore';
import {
  isPublicPath,
  resolvePostLoginDestination,
  DEFAULT_APP_HOME,
  consumePostLoginRedirect,
  captureLoginRedirectFromLocation,
} from "../../config/routes";
import apiClient, { authService } from "./hooks/AuthService";
import { syncOnboardingFlagFromServer } from "../../utils/onboardingSync";

const AuthContext = createContext({});

export const AuthContextProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const authEpochRef = useRef(0);

  useEffect(() => {
    captureLoginRedirectFromLocation(location.search);
  }, [location.pathname, location.search]);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const bumpAuthEpoch = () => {
    authEpochRef.current += 1;
    return authEpochRef.current;
  };

  const syncUser = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    setIsLoading(false);
    if (userData) {
      localStorage.setItem("userProfile", JSON.stringify(userData));
    }
  };

  const checkAuthStatus = async () => {
    const epoch = authEpochRef.current;
    try {
      if (!getAccessToken() && getRefreshToken()) {
        try {
          await authService.refresh();
        } catch {
          /* getUser + interceptor may still recover */
        }
      }
      const response = await authService.getUser({ _authSessionProbe: true });
      if (epoch !== authEpochRef.current) return;
      syncUser(response.data);
      await syncOnboardingFlagFromServer(apiClient);
    } catch (error) {
      if (epoch !== authEpochRef.current) return;
      console.error("Authentication check failed:", error);
      setUser(null);
      setIsAuthenticated(false);
      clearAuthTokens();
      localStorage.removeItem("userProfile");
      if (!isPublicPath(location.pathname)) {
        redirectToLogin();
      }
    } finally {
      if (epoch === authEpochRef.current) {
        setIsLoading(false);
      }
    }
  };

  const redirectToLogin = () => {
    if (!window.location.pathname.includes("/login")) {
      const currentPath = window.location.pathname + window.location.search;
      navigate(`/login?redirect=${encodeURIComponent(currentPath)}`);
    }
  };

  const resolveRedirect = () => {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get("redirect");
    const fromSession = consumePostLoginRedirect();
    const fallback = sessionStorage.getItem("lastVisitedPage") || DEFAULT_APP_HOME;
    return resolvePostLoginDestination(fromQuery || fromSession || fallback);
  };

  const finishAuthRedirect = () => {
    const redirectPath = resolveRedirect();
    sessionStorage.setItem("lastVisitedPage", redirectPath);
    navigate(redirectPath, { replace: true });
    window.setTimeout(() => {
      const stillMarketing =
        window.location.pathname === '/product'
        || window.location.pathname === '/'
        || window.location.pathname.startsWith('/login');
      if (stillMarketing) {
        window.location.assign(redirectPath);
      }
    }, 100);
  };

  const signUp = async (credentials) => {
    try {
      const signUpResponse = await authService.signUp(credentials);
      applyAuthTokensFromResponse(signUpResponse.data);
      const userData = signUpResponse.data?.user || signUpResponse.data;
      bumpAuthEpoch();
      syncUser(userData);
      localStorage.removeItem("qnaread.onboardingDone");
      navigate("/onboarding", { replace: true });
      return { success: true };
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || "Signup failed";
      console.error("Signup error:", errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const login = async (credentials) => {
    try {
      const loginResponse = await authService.login(credentials);
      const data = loginResponse.data || {};
      if (data.mfaRequired && data.mfaToken) {
        return {
          success: true,
          mfaRequired: true,
          mfaToken: data.mfaToken,
          deviceId: credentials.deviceId,
        };
      }
      applyAuthTokensFromResponse(data);
      const userResponse = await authService.getUser();
      bumpAuthEpoch();
      syncUser(userResponse.data);
      await syncOnboardingFlagFromServer(apiClient);
      finishAuthRedirect();
      return { success: true, mfaRequired: false };
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || "Login failed";
      console.error("Login error:", errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const googleLogin = async (payload) => {
    try {
      const googleResponse = await authService.googleLogin(payload);
      applyAuthTokensFromResponse(googleResponse.data);
      const userData = googleResponse.data?.user;
      bumpAuthEpoch();
      if (userData) {
        syncUser(userData);
      } else {
        const userResponse = await authService.getUser();
        syncUser(userResponse.data);
      }
      await syncOnboardingFlagFromServer(apiClient);
      finishAuthRedirect();
      return { success: true };
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Google sign-in failed";
      return { success: false, error: errorMessage };
    }
  };

  const verifyMfaLogin = async ({ mfaToken, code, deviceId }) => {
    try {
      const mfaResponse = await authService.verifyMfa({ mfaToken, code, deviceId });
      applyAuthTokensFromResponse(mfaResponse.data);
      const userResponse = await authService.getUser();
      bumpAuthEpoch();
      syncUser(userResponse.data);
      await syncOnboardingFlagFromServer(apiClient);
      finishAuthRedirect();
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.error || "Invalid verification code";
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    bumpAuthEpoch();
    try {
      await authService.logout();
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
      authService.clearSession(navigate);
    }
  };

  const refreshUser = async () => {
    try {
      const userResponse = await authService.getUser();
      syncUser(userResponse.data);
    } catch {
      /* ignore */
    }
  };

  const hasRole = (role) => user?.roles?.includes(role);
  const canAccess = (permission) => user?.permissions?.includes(permission);

  const contextValue = {
    isAuthenticated,
    isLoading,
    user,
    login,
    googleLogin,
    verifyMfaLogin,
    signUp,
    logout,
    refreshUser,
    hasRole,
    canAccess,
    apiClient,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthContextProvider");
  }
  return context;
};

export default AuthContext;
